import { Injectable, inject } from '@angular/core';
import { BaseNotifier } from '../../shared/notifiers/base-notifier';
import { Ref } from '../../shared/providers/ref';
import { LayerRepositoryProvider } from '../../shared/providers/repository-providers';
import { LayerState } from '../states/layer-state';
import { UbigeoState } from '../states/ubigeo-state';
import { FullExtentState } from '../states/full-extent-state';
import { LoadingProvider } from '../../shared/providers/loading-provider';
import { MapProvider } from '../providers/map-provider';
import { UbigeoProvider } from '../providers/ubigeo-provider';
import { FullExtentProvider } from '../providers/full-extent-provider';
import { ArcGISLayerFactory } from '../factories/arcgis-layer-factory';
import { LayerEntity, LayerTypeEnum, ILayerRepository, LegendItem } from '@gishub/domain';
import Map from '@arcgis/core/Map';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import FeatureFilter from '@arcgis/core/layers/support/FeatureFilter';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';
import { ArcgisDomainService } from '../../services/arcgis-domain-service';
import { ConfigService } from '../../services/config-service';
import { LocalInterventionService } from '../services/local-intervention-service';
import { INTERVENCIONES_SECTORIALES_LAYER_NAME, LOCAL_INTERVENTION_ICONS } from '../data/local-interventions';

const UPLOADED_CAT = 'Cargados';

@Injectable({ providedIn: 'root' })
export class LayerNotifier extends BaseNotifier<LayerState> {
  private readonly _arcgisDomain = inject(ArcgisDomainService);
  private readonly _config = inject(ConfigService);
  private get loading() { return this.ref.read(LoadingProvider); }

  constructor(ref: Ref) {
    super(ref, {
      layers: [],
      legends: {},
    });

    // Effect 1: Sincronizar capas en el mapa (SÍNCRONO, inmediato, sin fallos)
    this.ref.createEffect(() => {
      try {
        const mapState = this.ref.read(MapProvider).state();
        const regionState = this.ref.read(UbigeoProvider).state();
        const fullExtentState = this.ref.read(FullExtentProvider).state();
        const layerState = this.state();

        if (mapState.map && mapState.view && layerState?.layers?.length > 0) {
          this.syncMapLayers(mapState.map, layerState.layers, regionState, fullExtentState);
        }
      } catch (e) {
        console.error('[Effect1] Error:', e);
      }
    });

  }

  private get layerRepo(): ILayerRepository {
    return this.ref.read(LayerRepositoryProvider).instance;
  }

  private get layerFactory() {
    return this.ref.read(ArcGISLayerFactory);
  }

  override async build(): Promise<void> {
    this.loading.notifier.start();
    try {
      const res = await this.layerRepo.getAll();
      const layers = res.data ?? [];
      this._arcgisDomain.addDomainsFromLayers(layers, this._config.proxyUrl);
      this.loading.notifier.stop();
      this.setState({
        ...this.getStateValue(),
        layers,
      });
      // Efectos reactivos se encargarán de precargar geometría + sincronizar capas
    } catch (ex: any) {
      console.error('Error loading layers', ex);
      this.loading.notifier.stop();
    }
  }

  toggleLayerVisibility(name: string, visible: boolean): void {
    this.updateState(s => {
      const updatedLayers: LayerEntity[] = s.layers.map((category: LayerEntity) => {
        if (category.name === name) {
          return new LayerEntity({
            ...category,
            visible,
            layers: category.layers?.map((layer: LayerEntity) => new LayerEntity({ ...layer, visible }))
          });
        }

        if (category.layers) {
          const hasLayer = category.layers.some((l: LayerEntity) => l.name === name);
          if (hasLayer) {
            const newChildLayers: LayerEntity[] = category.layers.map((layer: LayerEntity) =>
              layer.name === name ? new LayerEntity({ ...layer, visible }) : layer
            );
            const anyChildVisible = newChildLayers.some((l: LayerEntity) => l.visible);
            return new LayerEntity({
              ...category,
              visible: anyChildVisible ? true : category.visible,
              layers: newChildLayers
            });
          }
        }
        return category;
      });

      return { ...s, layers: updatedLayers };
    });
  }

  addUploadedLayer(entity: LayerEntity): void {
    this._arcgisDomain.addDomainsFromLayers([entity], this._config.proxyUrl);
    this.updateState(s => {
      const existingCat = s.layers.find(c => c.name === UPLOADED_CAT);

      if (!existingCat) {
        return {
          ...s,
          layers: [...s.layers, {
            name: UPLOADED_CAT,
            type: LayerTypeEnum.Group,
            visible: true,
            layers: [entity],
          }],
        };
      }

      const exists = existingCat.layers?.find((l: LayerEntity) => l.name === entity.name);
      if (exists) return s;

      const layers: LayerEntity[] = s.layers.map((c: LayerEntity) =>
        c.name === UPLOADED_CAT
          ? new LayerEntity({ ...c, layers: [...(c.layers ?? []), entity] })
          : c
      );
      return { ...s, layers };
    });
  }

  removeUploadedLayer(name: string): void {
    this._removeLayerFromMap(name);

    this.updateState(s => ({
      ...s,
      layers: s.layers
        .map((c: LayerEntity) =>
          c.name === UPLOADED_CAT
            ? new LayerEntity({ ...c, layers: c.layers?.filter((l: LayerEntity) => l.name !== name) })
            : c
        )
        .filter((c: LayerEntity) => c.name !== UPLOADED_CAT || (c.layers && c.layers.length > 0)),
    }));
  }

  zoomToLayer(name: string): void {
    const mapState = this.ref.read(MapProvider).state();
    if (!mapState.map || !mapState.view) return;
    const layer = mapState.map.findLayerById(name) as any;
    if (!layer) return;
    layer.when(() => {
      if (layer.fullExtent) mapState.view!.goTo(layer.fullExtent).catch(() => {});
    });
  }

  setLayerOpacity(name: string, opacity: number): void {
    const clamped = Math.max(0, Math.min(1, opacity));

    this.updateState(s => ({
      ...s,
      layers: s.layers.map((cat: LayerEntity) =>
        cat.name === name
          ? new LayerEntity({ ...cat, opacity: clamped })
          : new LayerEntity({
              ...cat,
              layers: cat.layers?.map((l: LayerEntity) =>
                l.name === name ? new LayerEntity({ ...l, opacity: clamped }) : l
              ),
            })
      ),
    }));

    const mapLayer = this.ref.read(MapProvider).state().map?.findLayerById(name) as any;
    if (mapLayer) mapLayer.opacity = clamped;
  }

  /**
   * Carga la leyenda de una capa desde su endpoint /legend?f=json.
   * - La construcción de la URL (con o sin proxy) ocurre aquí, junto a ConfigService.
   * - El HTTP call se delega al repositorio (this.layerRepo.getLegend), siguiendo el
   *   mismo patrón que build() → this.layerRepo.getAll().
   */
  async loadLegend(layer: LayerEntity): Promise<void> {
    if (!layer.url || this.getStateValue().legends[layer.name]) return;

    const match = layer.url.match(/^(https?:\/\/.*?\/MapServer)(?:\/(\d+))?/i);
    if (!match) return;
    const baseUrl = match[1];
    const sublayerId = match[2];

    const legendUrl = `${baseUrl}/legend?f=json`;
    let fetchUrl = legendUrl;

    if (layer.hasProxy) {
      // Mismo patrón que ArcgisDomainService: cleanProxyUrl + '?' + targetUrl
      const cleanProxy = this._config.proxyUrl
        .replace(/\?.*$/, '')
        .replace(/\/+$/, '');
      fetchUrl = `${cleanProxy}?${legendUrl}`;
    }

    // Delegar el HTTP call al repositorio
    const res = await this.layerRepo.getLegend(fetchUrl, sublayerId);
    if (res.success && res.data && res.data.length > 0) {
      const items: LegendItem[] = res.data;
      this.updateState(s => ({
        ...s,
        legends: { ...s.legends, [layer.name]: items },
      }));
    }
  }

  private _syncLayerScale(name: string, minScale: number, maxScale: number): void {
    if (minScale === 0 && maxScale === 0) return;
    this.updateState(s => ({
      ...s,
      layers: s.layers.map((cat: LayerEntity) => ({
        ...cat,
        layers: cat.layers?.map((l: LayerEntity) =>
          l.name === name ? new LayerEntity({ ...l, minScale, maxScale }) : l
        )
      }))
    }));
  }

  private _removeLayerFromMap(name: string): void {
    const map = this.ref.read(MapProvider).state().map;
    if (!map) return;
    const layer = map.findLayerById(name);
    if (layer) map.remove(layer);
  }

  private async _applyServerSideFilter(entity: LayerEntity, mapLayer: any, geometry: any): Promise<void> {
    if (!geometry?.extent) return;
    
    // Nueva caché robusta: comparar los límites (xmin, ymin, xmax, ymax)
    const ext = geometry.extent;
    const currentExt = `${ext.xmin},${ext.ymin},${ext.xmax},${ext.ymax}`;
    
    if (mapLayer.__lastFilterExtent === currentExt) {
        return;
    }
    mapLayer.__lastFilterExtent = currentExt;

    if (!mapLayer.createQuery) return;

    this.loading.notifier.start();

    try {
      await mapLayer.when();
      const oidField = mapLayer.objectIdField;
      if (!oidField) return;

      const allIds: number[] = [];
      let start = 0;
      const batchSize = Math.min(mapLayer.maxRecordCount || 2000, 4000);
      let exceeded = false;
      const MAX_IDS = 50000;

      do {
        const query = mapLayer.createQuery();
        query.geometry = geometry;
        query.spatialRelationship = 'intersects';
        query.returnGeometry = false;
        query.outFields = [oidField];
        query.maxRecordCount = batchSize;
        if (start > 0) query.start = start;

        const result = await mapLayer.queryFeatures(query);
        for (const f of result.features) {
          const id = f.attributes[oidField];
          if (id != null) allIds.push(id);
        }

        exceeded = !!result.exceededTransferLimit;
        start += result.features.length;
      } while (exceeded && allIds.length < MAX_IDS);

      if (allIds.length > 0) {
        mapLayer.definitionExpression = `${oidField} IN (${allIds.join(',')})`;
        console.log(`[LayerNotifier] ${entity.name} - Filtro aplicado: ${allIds.length} features`);
      } else {
        console.warn(`[LayerNotifier] ${entity.name} - Consulta espacial devolvió 0. Limpiando filtro anterior.`);
        mapLayer.definitionExpression = undefined; 
      }
      
    } catch (err) {
      console.error(`[LayerNotifier] Error severo en filtro server-side para "${entity.name}":`, err);
    } finally {
      this.loading.notifier.stop();
    }
  }

  /**
   * Sincroniza capas en el mapa.
   */
  private syncMapLayers(map: Map, categories: LayerEntity[], regionState: UbigeoState, fullExtentState: FullExtentState): void {
    const selectedCode = regionState.selectedDistrict || regionState.selectedProvince || regionState.selectedDepartment;
    const selectedLabel = regionState.selectedDistrictName || regionState.selectedProvinceName || regionState.selectedDepartmentName || 'TODO';
    const view = this.ref.read(MapProvider).state().view;
    const geometry = fullExtentState.geometry;
    
    // Reordenar capas para que departamento esté SIEMPRE encima de provincia y distrito
    const departamentalLayer = map.findLayerById('Límite departamental');
    const provincialLayer = map.findLayerById('Límite provincial');
    const distritalLayer = map.findLayerById('Límite distrital');
    
    // Mover departamento al final (top) para que esté encima de todo
    if (departamentalLayer) {
      map.reorder(departamentalLayer, map.layers.length - 1);
    }
    // Provincia encima de distrito
    if (provincialLayer && distritalLayer) {
      const provIdx = map.layers.indexOf(provincialLayer);
      const distIdx = map.layers.indexOf(distritalLayer);
      if (provIdx < distIdx) {
        map.reorder(provincialLayer, distIdx);
      }
    }

    for (const category of categories) {
      if (category.layers) {
        category.layers.forEach((layerEntity: LayerEntity) => {
          if (layerEntity.filterWithGeometry) {
            if (geometry) {
              layerEntity.setValue(geometry);
            }
          } else {
            let value = selectedCode;
            // La capa departamental siempre debe usar el código de departamento
            if (layerEntity.name === 'Límite departamental' && regionState.selectedDepartment) {
              value = regionState.selectedDepartment;
            }
            if (value) {
              const label = layerEntity.definitionQueryWithLabel
                ? (regionState.selectedDepartmentName || 'TODO')
                : selectedLabel;
              layerEntity.setValue(layerEntity.definitionQueryWithLabel ? label : value);
            }
          }

          let mapLayer = map.findLayerById(layerEntity.name) as any;

          const effectiveVisible = category.visible && layerEntity.visible;

          if (!mapLayer && layerEntity.url && effectiveVisible) {
            mapLayer = this.layerFactory.create(layerEntity);
            if (mapLayer) {
              map.add(mapLayer);
              mapLayer.when(() => {
                this._syncLayerScale(layerEntity.name, mapLayer.minScale, mapLayer.maxScale);
              });
            }
          }

            if (mapLayer) {
              this._syncLocalInterventions(map, layerEntity, effectiveVisible);
              if (mapLayer.visible !== effectiveVisible) {
                console.log(`[LayerNotifier] Cambiando visibilidad de ${layerEntity.name} a ${effectiveVisible}`);
                mapLayer.visible = effectiveVisible;
              }

              if (mapLayer.opacity !== layerEntity.opacity) {
                mapLayer.opacity = layerEntity.opacity;
              }

              if (mapLayer.hasOwnProperty('definitionExpression')) {
                if (layerEntity.filterWithGeometry) {
                  // filterWithGeometry usa su propio mechanismo asíncrono
                  // para establecer definitionExpression con OBJECTIDs.
                  // No tocar aquí para no pisar el filtro espacial.
                } else {
                  const newExpr = layerEntity.definitionQuery || undefined;
                  if (mapLayer.definitionExpression !== newExpr) {
                    mapLayer.definitionExpression = newExpr;
                  }
                }
              }

              // Aplicar filtro server-side (robusto para 2D y 3D) - Solo a nivel Departamental
              const isDepartmentLevel = regionState.selectedDepartment && !regionState.selectedProvince && !regionState.selectedDistrict;

              if (layerEntity.filterWithGeometry && geometry) {
                if (isDepartmentLevel) {
                  this._applyServerSideFilter(layerEntity, mapLayer, geometry);
                } else {
                  // Si bajamos a nivel Provincia/Distrito, limpiar el filtro espacial
                  if (mapLayer.definitionExpression?.includes('IN (')) {
                      mapLayer.definitionExpression = undefined;
                  }
                  mapLayer.__lastFilterExtent = undefined;
                }
              }

              // Actualizar sublayers de MapImageLayer (no tienen definitionExpression propia)
              if (mapLayer.type === 'map-image' && layerEntity.url) {
                const sublayerMatch = layerEntity.url.match(/\/(\d+)$/);
                if (sublayerMatch) {
                  const subId = parseInt(sublayerMatch[1], 10);
                  const sublayer = (mapLayer as MapImageLayer).findSublayerById(subId);
                  if (sublayer) {
                  const sublayer = (mapLayer as MapImageLayer).findSublayerById(subId);
                  if (sublayer) {
                     // Fix: sincronizar visibilidad de la subcapa
                    if (sublayer.visible !== effectiveVisible) {
                      sublayer.visible = effectiveVisible;
                    }
                    
                    const newExpr = layerEntity.definitionQuery || undefined;
                    console.log(`[LayerNotifier] Actualizando ${layerEntity.name} sublayer ${subId}, expr: ${newExpr}`);
                    
                    // Aplicar filtro espacial si aplica
                    if (isDepartmentLevel && layerEntity.filterWithGeometry) {
                       this._applyServerSideFilter(layerEntity, sublayer, geometry);
                    } else if (sublayer.definitionExpression !== newExpr) {
                       sublayer.definitionExpression = newExpr;
                    }
                  }
                  }
                }
              }

            }
        });
      }
    }
  }

  /**
   * Pinta las inversiones locales (Chupaca) como un GraphicsLayer cuando la capa
   * "Intervenciones sectoriales en comunidades" está visible en el mapa.
   */
  private _syncLocalInterventions(map: Map, layerEntity: LayerEntity, visible: boolean): void {
    if (layerEntity.name !== INTERVENCIONES_SECTORIALES_LAYER_NAME) return;

    const id = 'intervenciones-locales';
    let layer = map.findLayerById(id) as any;
    if (!visible) {
      if (layer) layer.visible = false;
      return;
    }
    if (!layer) {
      layer = new GraphicsLayer({ id, listMode: 'hide' });
      map.add(layer);
    }
    layer.visible = true;
    if (layer.__localLoaded) return;
    layer.__localLoaded = true;

    const features = this.ref.read(LocalInterventionService).getLocalFeatures();
    const graphics = features.map((f: any) => {
      const [x, y] = f.geometry.coordinates;
      const tipo = f.attributes['tipo_intervencion'] ?? '';
      const icon = LOCAL_INTERVENTION_ICONS[tipo] ?? LOCAL_INTERVENTION_ICONS['Proyecto de Inversión'];
      return new Graphic({
        attributes: { ...f.attributes },
        geometry: new Point({ x, y, spatialReference: { wkid: 4326 } }),
        symbol: new PictureMarkerSymbol({
          url: icon,
          width: 11,
          height: 20,
        }),
      });
    });
    layer.addMany(graphics);
  }

}